import React, { useCallback, useEffect, useState } from 'react';

import { Container, makeStyles } from '@material-ui/core';
import { Add as PlusIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import FooterButton from 'components/FooterButton';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import { parseFiltersForActionTemplates } from 'config/api/actionLibrary/actionLibrary.parsers';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';
import { useHelpDialogContext } from 'reactContext/HelpDialogContext/HelpDialogContext';
import PATHS from 'router/PATHS';
import {
  getPurposeFilter,
  getAreaFilter,
  getCriticalityFilter,
  getCreatorFilter,
  getSubareaFilter,
  getStatusFilter,
} from 'services/_filters/parsers/actionTemplateFilters';
import type { FilterState, AvailableFilters } from 'types/ActionLibrary';

import ActionTemplatesTable from './_components/ActionTemplatesTable/ActionTemplatesTable';
import EmptyState from './_components/EmptyState/EmptyState';
import ErrorState from './_components/ErrorState/ErrorState';

const useStyles = makeStyles(theme => ({
  createButton: {
    marginBottom: theme.spacing(2),
  },
  iconInButton: {
    marginRight: theme.spacing(1),
  },
}));

const ActionLibraryPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const styles = useStyles();
  const { showHelpDialog } = useHelpDialogContext();

  const [filters, setFilters] = useState<FilterState>({});
  const [availableFilters, setAvailableFilters] = useState<AvailableFilters | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [sort, setSort] = useState<{ key: string | null; direction: string | null }>({
    key: null,
    direction: null,
  });
  const [tableData, setTableData] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const pageSize = 20;

  const onSort = (key: string, direction: string) => {
    setSort({ key, direction });
  };

  const loadTableData = useCallback(
    async (offset = 0, search = '', sortParams = sort) => {
      try {
        setLoading(true);
        setError(null); // Clear any previous errors

        // Build ordering parameter for API
        let ordering: string | undefined;
        if (sortParams.key && sortParams.direction) {
          const prefix = sortParams.direction === 'desc' ? '-' : '';
          ordering = `${prefix}${sortParams.key}`;
        }

        // Parse filters from frontend format (camelCase) to API format (snake_case)
        const parsedFilters = parseFiltersForActionTemplates(filters);

        const params: any = {
          limit: pageSize,
          offset,
          ...(search && { search }),
          ...(ordering && { ordering }),
          ...parsedFilters,
        };

        const { data } = await actionTemplatesApi.getAllActionTemplates(params);

        // Ensure we always have an array, even if empty
        setTableData(Array.isArray(data.results) ? data.results : []);
        setHasNextPage(data.next !== null);
        setCurrentOffset(offset);
      } catch (err) {
        setError(err as Error);
        setTableData([]); // Set empty array on error to prevent showing stale data
      } finally {
        setLoading(false);
      }
    },
    [filters, sort],
  );

  const getTableData = useCallback(() => loadTableData(currentOffset + pageSize, searchTerm), [currentOffset, searchTerm, loadTableData]);

  const refreshData = useCallback(() => loadTableData(0, searchTerm), [searchTerm, loadTableData]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [purposeFilter, areaFilter, criticalityFilter, creatorFilter, statusFilter, subareaFilter] = await Promise.all([
          getPurposeFilter(),
          getAreaFilter(),
          getCriticalityFilter(),
          getCreatorFilter(),
          getStatusFilter(),
          getSubareaFilter(), // Initial load without area - will be empty
        ]);

        setAvailableFilters({
          purpose: purposeFilter,
          area: areaFilter,
          subarea: subareaFilter,
          status: statusFilter,
          criticality: criticalityFilter,
          creator: creatorFilter,
        });
      } catch (err) {
        setError(err as Error);
      }
    };

    loadFilters();
  }, []);

  // Update subarea filter when area filter changes
  useEffect(() => {
    const updateSubareaFilter = async () => {
      if (!availableFilters) return;

      const selectedAreas = filters.area || [];
      if (selectedAreas.length > 0) {
        // Get subareas for all selected areas
        // Filters can be either string[] or Array<{id: string, name: string}>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const areaIds = selectedAreas.map(area => ((area as any)?.id || (area as any)?.name || area) as string);

        if (areaIds.length > 0) {
          try {
            // Fetch subareas for all selected areas in parallel
            const subareaFiltersResults = await Promise.all(areaIds.map(id => getSubareaFilter(id)));

            // Combine all filters
            const allFilters = subareaFiltersResults.flatMap(result => result.filters);

            // Remove duplicates based on id
            const uniqueFiltersMap = new Map();
            allFilters.forEach(filter => {
              if (!uniqueFiltersMap.has(filter.id)) {
                uniqueFiltersMap.set(filter.id, filter);
              }
            });

            const uniqueFilters = Array.from(uniqueFiltersMap.values());

            const combinedSubareaFilter = {
              sectionName: action_library_messages.filters.subarea,
              filters: uniqueFilters,
            };

            setAvailableFilters(prev => {
              if (!prev) return null;
              // Only update if subarea filter actually changed
              if (JSON.stringify(prev.subarea.filters) === JSON.stringify(combinedSubareaFilter.filters)) {
                return prev;
              }
              return { ...prev, subarea: combinedSubareaFilter };
            });
          } catch (err) {
            // If error, set empty subarea filter
            setAvailableFilters(prev => {
              if (!prev) return null;
              if (prev.subarea.filters.length === 0) return prev;
              return {
                ...prev,
                subarea: {
                  sectionName: action_library_messages.filters.subarea,
                  filters: [],
                },
              };
            });
          }
        }
      } else {
        // Clear subarea filter when no area is selected
        setAvailableFilters(prev => {
          if (!prev) return null;
          if (prev.subarea.filters.length === 0) return prev;
          return {
            ...prev,
            subarea: {
              sectionName: action_library_messages.filters.subarea,
              filters: [],
            },
          };
        });
        // Also clear subarea from filters when area is cleared
        setFilters(prev => {
          if (!prev.subarea) return prev;
          const newFilters = { ...prev };
          delete newFilters.subarea;
          return newFilters;
        });
      }
    };

    updateSubareaFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.area]);

  // Initial data load and reload when filters or sort change
  useEffect(() => {
    if (availableFilters) {
      loadTableData(0, searchTerm, sort);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFilters, filters, sort, searchTerm]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    loadTableData(0, term);
  };

  const handleCreateClick = () => {
    history.push(PATHS.ACTION_LIBRARY_CREATE);
  };

  const openHelpDialog = () => {
    showHelpDialog({ title: t(action_library_messages.help.title), body: t(action_library_messages.help.body) });
  };

  const handleResetFilters = () => {
    setFilters({});
    setSearchTerm('');
    loadTableData(0, '');
  };

  if (error) {
    return (
      <Container>
        <ErrorState onRetry={() => window.location.reload()} />
      </Container>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader
          rightAdornment={
            <>
              <TextButton onClick={openHelpDialog}>{t(general_messages.how_it_works)}</TextButton>
              <ColoredButton
                className={styles.createButton}
                customColor='secondary'
                disabled={false}
                icon={false}
                onClick={handleCreateClick}
                showLoader={false}
                textLike={false}
                variant='outlined'
              >
                <PlusIcon className={styles.iconInButton} fontSize='small' />
                {t(action_library_messages.create_button)}
              </ColoredButton>
            </>
          }
        >
          {t(action_library_messages.page_title)}
        </ManagePageHeader>

        {!availableFilters ? (
          <Loader inner />
        ) : (
          <>
            <ActionTemplatesTable
              data={tableData}
              filtersService={{ availableFilters, filters, setFilters }}
              loading={loading}
              onSearch={handleSearch}
              onSort={onSort}
              refreshData={refreshData}
              searchValue={searchTerm}
              sort={sort}
            />
            {tableData && tableData.length === 0 && !loading && <EmptyState onResetFilters={handleResetFilters} />}

            {hasNextPage && (
              <FooterButton className='' loading={loading} onClick={getTableData}>
                {t(action_library_messages.table.show_more)}
              </FooterButton>
            )}
          </>
        )}
      </Container>
    </ErrorBoundary>
  );
};

export default ActionLibraryPage;
